import React, { useState } from 'react';
import { Typography, Image, Button, Tooltip, message as antMessage } from 'antd';
import { FileOutlined, FilePdfOutlined, FileImageOutlined, CopyOutlined } from '@ant-design/icons';
import isEmpty from 'lodash/isEmpty';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

const USER_BUBBLE_BG = '#000000';
const USER_BUBBLE_FG = '#ffffff';

/**
 * Render a single content part based on type
 */
function ContentPart({ part, index, isUser }) {
  if (!part || !part.type) return null;

  const linkColor = isUser ? '#ffffff' : '#1890ff';
  const codeBg = isUser ? 'rgba(255,255,255,0.18)' : '#f2f1ed';

  switch (part.type) {
    case 'text':
      return (
        <div key={index} style={{ fontSize: '14px', margin: 0, minWidth: 0, maxWidth: '100%', overflow: 'hidden', color: isUser ? USER_BUBBLE_FG : '#1f2937' }}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              p: ({ node, ...props }) => <p style={{ margin: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word', maxWidth: '100%' }} {...props} />,
              a: ({ node, ...props }) => <a style={{ color: linkColor, textDecoration: 'underline', overflowWrap: 'anywhere', wordBreak: 'break-all' }} target="_blank" rel="noopener noreferrer" {...props} />,
              ul: ({ node, ...props }) => <ul style={{ paddingLeft: '20px', margin: '8px 0', maxWidth: '100%' }} {...props} />,
              ol: ({ node, ...props }) => <ol style={{ paddingLeft: '20px', margin: '8px 0', maxWidth: '100%' }} {...props} />,
              li: ({ node, ...props }) => <li style={{ marginBottom: '4px', overflowWrap: 'anywhere', wordBreak: 'break-word' }} {...props} />,
              table: ({ node, ...props }) => (
                <div style={{ overflowX: 'auto', maxWidth: '100%', margin: '8px 0' }}>
                  <table style={{ borderCollapse: 'collapse', fontSize: '13px' }} {...props} />
                </div>
              ),
              code: ({ node, inline, className, children, ...props }) => {
                return !inline ? (
                  <pre style={{ background: codeBg, padding: '12px', borderRadius: '6px', overflowX: 'auto', margin: '8px 0', maxWidth: '100%', boxSizing: 'border-box' }}>
                    <code className={className} style={{ display: 'block', whiteSpace: 'pre', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '12.5px' }} {...props}>
                      {children}
                    </code>
                  </pre>
                ) : (
                  <code style={{ background: codeBg, padding: '2px 4px', borderRadius: '4px', fontSize: '12px', overflowWrap: 'anywhere', wordBreak: 'break-all' }} {...props}>
                    {children}
                  </code>
                )
              }
            }}
          >
            {part.text}
          </ReactMarkdown>
        </div>
      );

    case 'image_url':
      return (
        <div key={index}>
          <Image
            src={part.image_url?.url}
            alt="Attachment"
            style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 8, margin: 0 }}
            preview={{ mask: 'Click to preview' }}
          />
        </div>
      );

    case 'file':
      return (
        <div
          key={index}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '8px 12px',
            marginTop: 8,
            backgroundColor: isUser ? 'rgba(255,255,255,0.15)' : '#f2f1ed',
            borderRadius: 8,
            border: isUser ? '1px solid rgba(255,255,255,0.25)' : '1px solid #e5e4df'
          }}
        >
          <FileIcon contentType={part.file?.content_type} />
          <a
            href={part.file?.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: '13px', marginLeft: 8, color: isUser ? '#ffffff' : '#1890ff', textDecoration: 'underline' }}
          >
            {part.file?.filename || 'Download file'}
          </a>
        </div>
      );

    default:
      return null;
  }
}

function FileIcon({ contentType }) {
  if (!contentType) return <FileOutlined style={{ fontSize: 18, color: '#8c8c8c' }} />;
  if (contentType.includes('pdf')) return <FilePdfOutlined style={{ fontSize: 18, color: '#ff4d4f' }} />;
  if (contentType.startsWith('image/')) return <FileImageOutlined style={{ fontSize: 18, color: '#52c41a' }} />;
  return <FileOutlined style={{ fontSize: 18, color: '#8c8c8c' }} />;
}

const UserMessage = ({ part, idx }) => (
  <div style={{ display: 'flex', marginBottom: '16px', justifyContent: 'flex-end', minWidth: 0, maxWidth: '100%' }}>
    <div
      style={{
        maxWidth: '75%',
        minWidth: 0,
        padding: '10px 16px',
        borderRadius: '20px',
        backgroundColor: USER_BUBBLE_BG,
        color: USER_BUBBLE_FG,
        overflow: 'hidden',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
      }}
    >
      <ContentPart part={part} index={idx} isUser />
    </div>
  </div>
);

const AssistantMessage = ({ part, idx }) => (
  <div style={{ display: 'flex', marginBottom: '20px', minWidth: 0, maxWidth: '100%' }}>
    <div style={{ maxWidth: '90%', minWidth: 0, padding: '2px 4px', overflow: 'hidden' }}>
      <ContentPart part={part} index={idx} isUser={false} />
    </div>
  </div>
);


const normalizeContent = (content) => {
  if (!content) return [];

  if (Array.isArray(content)) {
    return content.map(part => {
      if (typeof part === 'string') {
        return { type: 'text', text: part };
      }
      if (part && typeof part === 'object' && part.type) {
        return part;
      }
      return { type: 'text', text: String(part) };
    });
  }

  if (typeof content === 'string') {
    return [{ type: 'text', text: content }];
  }

  if (typeof content === 'object' && content.type) {
    return [content];
  }

  return [];
};

/**
 * ChatMessage - Single chat message. User gets a black pill bubble;
 * assistant gets plain text on the page background.
 * Hover anywhere on the message → 複製 icon 從 opacity 0 淡入。
 */
export default function ChatMessage({ message }) {
  const { t } = useTranslation();
  const { role, content } = message;
  const isUser = role === 'user';
  const contentArray = normalizeContent(content);
  const [hovered, setHovered] = useState(false);

  if (isEmpty(contentArray)) return null;

  // Copy only text parts; skip image/file parts (URLs that aren't user-meaningful)
  const copyText = contentArray
    .filter((p) => p.type === 'text')
    .map((p) => p.text)
    .join('\n\n');

  const handleCopy = async () => {
    if (!copyText) return;
    try {
      // navigator.clipboard 只在 secure context (https / localhost) 可用；
      // dev 環境 http://0.0.0.0:3000 會 undefined → 走 execCommand fallback。
      if (navigator?.clipboard?.writeText && window.isSecureContext) {
        await navigator.clipboard.writeText(copyText);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = copyText;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.top = '-1000px';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (!ok) throw new Error('execCommand copy returned false');
      }
      antMessage.success(t('common.copied', '已複製'));
    } catch (err) {
      console.error('Copy failed:', err);
      antMessage.error(t('common.error.copy_failed', '複製失敗'));
    }
  };

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {contentArray.map((part, idx) => (
        isUser
          ? <UserMessage key={idx} part={part} idx={idx} />
          : <AssistantMessage key={idx} part={part} idx={idx} />
      ))}
      {copyText && (
        <div
          style={{
            display: 'flex',
            justifyContent: isUser ? 'flex-end' : 'flex-start',
            marginTop: -8,
            marginBottom: 8,
            paddingLeft: isUser ? 0 : 4,
            paddingRight: isUser ? 4 : 0
          }}
        >
          <Tooltip title={t('common.button.copy', '複製')}>
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined style={{ fontSize: 12 }} />}
              onClick={handleCopy}
              style={{
                height: 20,
                padding: '0 4px',
                color: 'rgba(0,0,0,0.45)',
                opacity: hovered ? 1 : 0,
                pointerEvents: hovered ? 'auto' : 'none',
                transition: 'opacity 0.15s'
              }}
            />
          </Tooltip>
        </div>
      )}
    </div>
  );
}
